<template>
    <div class="filtered-list-container">
        <div class="filter-summary mb-4">
            <div class="summary-header">
                <h3 class="summary-title">검색 결과: <strong>{{ schedules.length }}건</strong></h3>
                <vs-button
                    color="primary"
                    size="small"
                    icon-pack="feather"
                    icon="icon-printer"
                    @click="printSchedules"
                    :disabled="schedules.length === 0"
                    class="print-button"
                >
                    스케줄인쇄
                </vs-button>
            </div>
            <div class="filter-criteria">
                <span class="criteria-item">
                    <i class="feather icon-calendar"></i>
                    {{ formatDate(filterCriteria.date) }}
                </span>
                <span class="criteria-item">
                    <i class="feather icon-clock"></i>
                    {{ filterCriteria.hour === 'ALL' ? '전체 시간' : `${filterCriteria.hour}:00 - ${filterCriteria.hour}:59` }}
                </span>
                <span class="criteria-item" v-if="filterCriteria.medicalCd">
                    <i class="feather icon-home"></i>
                    {{ getMedicalName(filterCriteria.medicalCd) }}
                </span>
                <span class="criteria-item" v-else>
                    <i class="feather icon-home"></i>
                    전체 치료실
                </span>
            </div>
        </div>

        <div class="table-wrapper" v-if="schedules.length > 0">
            <table class="filtered-table border-collapse">
                <thead>
                    <tr class="table-header">
                        <th class="text-center">시간</th>
                        <th class="text-center">치료실</th>
                        <th>환자명</th>
                        <th class="text-center">병실</th>
                        <th>치료내용/메모</th>
                        <th class="text-center">담당자</th>
                        <th class="text-center">작업</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="schedule in sortedSchedules"
                        :key="schedule.SCHEDULE_SEQ"
                        :style="getScheduleRowStyle(schedule)"
                        class="schedule-row"
                    >
                        <!-- 시간 -->
                        <td class="text-center time-cell">
                            {{ formatTime(schedule.REAL_TIME_STRING) }}
                        </td>

                        <!-- 치료실 -->
                        <td class="text-center room-cell">
                            [{{ schedule.MEDICAL_NM ? schedule.MEDICAL_NM.substring(0,1) : '' }}]
                        </td>

                        <!-- 환자명 -->
                        <td class="patient-cell">
                            <a :href="'/patientSchedule/scheduleCalendar/' + schedule.PATIENT_UID" class="patient-link">
                                <template v-if="schedule.DC == 'Y'">
                                    <span class="cancelled-text">
                                        {{ (!schedule.STATUS_NAME || schedule.STATUS_NAME === '외래') ? '외래' : schedule.MEDICAL_ROOM_NUMBER_NM }} {{ schedule.PATIENT_USERNAME }}
                                    </span>
                                </template>
                                <template v-else>
                                    {{ (!schedule.STATUS_NAME || schedule.STATUS_NAME === '외래') ? '외래' : schedule.MEDICAL_ROOM_NUMBER_NM }} {{ schedule.PATIENT_USERNAME }}
                                </template>
                            </a>
                        </td>

                        <!-- 병실 -->
                        <td class="text-center ward-cell">
                            {{ schedule.MEDICAL_ROOM_NM }}
                        </td>

                        <!-- 치료내용/메모 -->
                        <td class="treatment-cell">
                            <template v-if="schedule.DC == 'Y'">
                                <span class="dc-memo">{{ schedule.DC_MEMO }}</span>
                            </template>
                            <template v-else>
                                <div class="treatment-details">
                                    <span v-if="schedule.managerNmList" class="detail-item">
                                        {{ schedule.managerNmList }}
                                    </span>
                                    <span v-if="schedule.detailNmList" class="detail-item">
                                        / {{ schedule.detailNmList }}
                                    </span>
                                    <span v-if="schedule.MEMO" class="detail-item">
                                        / {{ schedule.MEMO }}
                                    </span>
                                </div>
                            </template>
                        </td>

                        <!-- 담당자 -->
                        <td class="text-center staff-cell">
                            <template v-if="schedule.MEDICAL_MEMBER_USERNAME">
                                {{ schedule.MEDICAL_MEMBER_USERNAME.substring(0, 1) }}
                            </template>
                            <span v-if="schedule.WEST_MEDICAL_USERNAME"> / </span>
                            <template v-if="schedule.WEST_MEDICAL_USERNAME">
                                {{ schedule.WEST_MEDICAL_USERNAME.substring(0, 1) }}
                            </template>
                        </td>

                        <!-- 작업 -->
                        <td class="text-center action-cell">
                            <div class="action-buttons">
                                <a href="#" @click.prevent="$emit('member-view', schedule.PATIENT_UID)" title="환자 조회">
                                    <i class="feather icon-search action-icon view-icon"></i>
                                </a>
                                <a href="#" @click.prevent="$emit('schedule-edit', schedule)" title="수정">
                                    <i class="feather icon-edit-2 action-icon edit-icon"></i>
                                </a>
                                <a href="#" @click.prevent="$emit('schedule-delete', schedule)" title="삭제">
                                    <i class="feather icon-x action-icon delete-icon"></i>
                                </a>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div v-else class="no-results text-center">
            <i class="feather icon-alert-circle no-results-icon"></i>
            <p class="no-results-text">검색 조건에 맞는 스케줄이 없습니다.</p>
        </div>

        <!-- Hidden Print Template -->
        <div id="printArea" style="display: none;">
            <div class="print-container">
                <!-- Print Header -->
                <div class="print-header">
                    <h2 class="print-title">치료실 스케줄</h2>
                    <div class="print-criteria">
                        <div class="print-criteria-row">
                            <span class="print-label">조회 날짜:</span>
                            <span class="print-value">{{ formatDate(filterCriteria.date) }}</span>
                        </div>
                        <div class="print-criteria-row">
                            <span class="print-label">조회 시간:</span>
                            <span class="print-value">{{ filterCriteria.hour === 'ALL' ? '전체 시간' : `${filterCriteria.hour}:00 - ${filterCriteria.hour}:59` }}</span>
                        </div>
                        <div class="print-criteria-row">
                            <span class="print-label">치료실:</span>
                            <span class="print-value">
                                {{ filterCriteria.medicalCd ? getMedicalName(filterCriteria.medicalCd) : '전체 치료실' }}
                            </span>
                        </div>
                        <div class="print-criteria-row">
                            <span class="print-label">검색 결과:</span>
                            <span class="print-value">{{ schedules.length }}건</span>
                        </div>
                    </div>
                </div>

                <!-- Print Table -->
                <table class="print-table">
                    <thead>
                        <tr>
                            <th class="text-center">시간</th>
                            <th class="text-center">치료실</th>
                            <th>환자명</th>
                            <th class="text-center">병실</th>
                            <th>치료내용/메모</th>
                            <th class="text-center">담당자</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="schedule in sortedSchedules"
                            :key="schedule.SCHEDULE_SEQ"
                            :style="getScheduleRowStyle(schedule)"
                        >
                            <td class="text-center">{{ formatTime(schedule.REAL_TIME_STRING) }}</td>
                            <td class="text-center">[{{ schedule.MEDICAL_NM ? schedule.MEDICAL_NM.substring(0,1) : '' }}]</td>
                            <td>
                                <template v-if="schedule.DC == 'Y'">
                                    <span style="text-decoration: line-through; color: #999;">
                                        {{ (!schedule.STATUS_NAME || schedule.STATUS_NAME === '외래') ? '외래' : schedule.MEDICAL_ROOM_NUMBER_NM }} {{ schedule.PATIENT_USERNAME }}
                                    </span>
                                </template>
                                <template v-else>
                                    {{ (!schedule.STATUS_NAME || schedule.STATUS_NAME === '외래') ? '외래' : schedule.MEDICAL_ROOM_NUMBER_NM }} {{ schedule.PATIENT_USERNAME }}
                                </template>
                            </td>
                            <td class="text-center">{{ schedule.MEDICAL_ROOM_NM }}</td>
                            <td>
                                <template v-if="schedule.DC == 'Y'">
                                    <span style="color: #e74c3c; font-style: italic;">{{ schedule.DC_MEMO }}</span>
                                </template>
                                <template v-else>
                                    <span v-if="schedule.managerNmList">{{ schedule.managerNmList }}</span>
                                    <span v-if="schedule.detailNmList"> / {{ schedule.detailNmList }}</span>
                                    <span v-if="schedule.MEMO"> / {{ schedule.MEMO }}</span>
                                </template>
                            </td>
                            <td class="text-center">
                                <template v-if="schedule.MEDICAL_MEMBER_USERNAME">
                                    {{ schedule.MEDICAL_MEMBER_USERNAME.substring(0, 1) }}
                                </template>
                                <span v-if="schedule.WEST_MEDICAL_USERNAME"> / </span>
                                <template v-if="schedule.WEST_MEDICAL_USERNAME">
                                    {{ schedule.WEST_MEDICAL_USERNAME.substring(0, 1) }}
                                </template>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <!-- Print Footer -->
                <div class="print-footer">
                    <p>출력일시: {{ getCurrentDateTime() }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'FilteredScheduleList',
    props: {
        schedules: {
            type: Array,
            required: true
        },
        filterCriteria: {
            type: Object,
            required: true
        },
        treatmentRooms: {
            type: Array,
            default: () => []
        }
    },
    computed: {
        sortedSchedules() {
            return [...this.schedules].sort((a, b) => {
                return a.REAL_TIME_STRING.localeCompare(b.REAL_TIME_STRING);
            });
        }
    },
    methods: {
        formatDate(dateString) {
            if (!dateString || dateString.length !== 8) return dateString;
            const year = dateString.substring(0, 4);
            const month = dateString.substring(4, 6);
            const day = dateString.substring(6, 8);
            return `${year}-${month}-${day}`;
        },

        formatTime(timeString) {
            if (!timeString || timeString.length < 4) return timeString;
            return timeString.substring(0, 2) + ':' + timeString.substring(2, 4);
        },

        getMedicalName(medicalCd) {
            if (!medicalCd) return '전체 치료실';
            const room = this.treatmentRooms.find(r => r.MEDICAL_CD === medicalCd);
            return room ? room.MEDICAL_NM : medicalCd;
        },

        getScheduleRowStyle(schedule) {
            let btn_color = '';

            // Check for AWAIT_TIME (대기 시간)
            if (schedule.AWAIT_TIME && schedule.AWAIT_TIME.trim() !== '') {
                return 'background-color:rgba(102, 101, 101,0.3);';
            }

            // Color by medical type
            const medical_nm = schedule.MEDICAL_NM ? schedule.MEDICAL_NM.trim() : '';
            if (medical_nm == '고주파실') {
                btn_color = 'background-color:rgba(128,175,178, 0.3);';
            } else if (medical_nm == '물리치료실') {
                btn_color = 'background-color:rgba(150,180,214, 0.3);';
            } else if (medical_nm == '한의과 침치료') {
                btn_color = 'background-color:rgba(196,164,148, 0.3);';
            } else if (medical_nm == '주열실') {
                btn_color = 'background-color:rgba(201,149,164, 0.3);';
            } else if (medical_nm == '외진') {
                btn_color = 'background-color:rgba(179,167,206, 0.3);';
            } else if (medical_nm == '외출') {
                btn_color = 'background-color:rgba(185,232,239,0.3);';
            } else if (medical_nm == '말초신경클리닉') {
                btn_color = 'background-color:rgba(204,216,195,0.3);';
            } else if (medical_nm == '마사지실') {
                btn_color = 'background-color:rgba(224,227,154,0.3);';
            } else {
                btn_color = 'background-color:rgba(226,164,147,0.3);';
            }
            return btn_color;
        },

        printSchedules() {
            // Edge case: No results
            if (this.schedules.length === 0) {
                return;
            }

            // Generate print content HTML
            const printContent = document.getElementById('printArea').innerHTML;

            // Create new window
            const printWindow = window.open('', '_blank', 'width=1200,height=800');

            // Write HTML to new window
            printWindow.document.write(`
                <!DOCTYPE html>
                <html>
                <head>
                    <meta charset="UTF-8">
                    <title>치료실 스케줄 - ${this.formatDate(this.filterCriteria.date)}</title>
                    <style>
                        /* Reset & Base Styles */
                        * {
                            margin: 0;
                            padding: 0;
                            box-sizing: border-box;
                        }

                        body {
                            font-family: 'Malgun Gothic', '맑은 고딕', Arial, sans-serif;
                            padding: 20px;
                            color: #333;
                        }

                        /* Print Container */
                        .print-container {
                            max-width: 1100px;
                            margin: 0 auto;
                        }

                        /* Print Header */
                        .print-header {
                            margin-bottom: 20px;
                            border-bottom: 2px solid #439798;
                            padding-bottom: 15px;
                        }

                        .print-title {
                            font-size: 24px;
                            font-weight: bold;
                            color: #439798;
                            margin-bottom: 15px;
                        }

                        .print-criteria {
                            display: grid;
                            grid-template-columns: repeat(2, 1fr);
                            gap: 10px;
                            background-color: #f8f9fa;
                            padding: 15px;
                            border-radius: 4px;
                        }

                        .print-criteria-row {
                            display: flex;
                            gap: 10px;
                        }

                        .print-label {
                            font-weight: 600;
                            color: #666;
                            min-width: 80px;
                        }

                        .print-value {
                            color: #333;
                        }

                        /* Print Table */
                        .print-table {
                            width: 100%;
                            border-collapse: collapse;
                            margin-bottom: 20px;
                            font-size: 13px;
                        }

                        .print-table th {
                            background-color: #f1f3f4;
                            padding: 12px 10px;
                            text-align: left;
                            font-weight: 600;
                            border: 1px solid #ddd;
                            color: #333;
                            white-space: nowrap;
                        }

                        .print-table td {
                            padding: 10px;
                            border: 1px solid #ddd;
                            vertical-align: middle;
                            word-wrap: break-word;
                        }

                        .print-table .text-center {
                            text-align: center;
                        }

                        /* Print Footer */
                        .print-footer {
                            margin-top: 20px;
                            padding-top: 10px;
                            border-top: 1px solid #ddd;
                            text-align: right;
                            font-size: 12px;
                            color: #666;
                        }

                        /* Print Media Query */
                        @media print {
                            @page {
                                size: A4 landscape;
                                margin: 15mm;
                            }

                            body {
                                padding: 0;
                            }

                            .print-container {
                                max-width: 100%;
                            }

                            .print-table {
                                font-size: 12px;
                            }

                            .print-table th,
                            .print-table td {
                                padding: 8px 6px;
                            }
                        }
                    </style>
                </head>
                <body>
                    ${printContent}
                </body>
                </html>
            `);

            printWindow.document.close();

            // Wait for content to load, then print
            printWindow.onload = function() {
                printWindow.focus();
                printWindow.print();
                // Don't close automatically - let user preview
            };
        },

        getCurrentDateTime() {
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day} ${hours}:${minutes}`;
        }
    }
};
</script>

<style scoped>
.filtered-list-container {
    padding: 1.5rem;
    background: #fff;
    border-radius: 8px;
}

/* Filter Summary */
.filter-summary {
    background-color: #f8f9fa;
    padding: 1rem 1.5rem;
    border-radius: 6px;
    border-left: 4px solid #439798;
}

.summary-title {
    font-size: 1.2rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 0.5rem;
}

.summary-title strong {
    color: #439798;
}

.filter-criteria {
    display: flex;
    gap: 1.5rem;
    flex-wrap: wrap;
}

.criteria-item {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    color: #666;
}

.criteria-item i {
    color: #439798;
    font-size: 1rem;
}

/* Table Styles */
.table-wrapper {
    overflow-x: auto;
    margin-top: 1rem;
}

.filtered-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;
}

.filtered-table th {
    background-color: #f1f3f4;
    padding: 12px 10px;
    text-align: left;
    font-weight: 600;
    border: 1px solid #ddd;
    color: #333;
    white-space: nowrap;
}

.filtered-table td {
    padding: 10px;
    border: 1px solid #ddd;
    vertical-align: middle;
}

.schedule-row {
    transition: all 0.2s ease;
}

.schedule-row:hover {
    filter: brightness(0.95);
}

/* Cell Specific Styles */
.time-cell {
    font-weight: 600;
    color: #439798;
    white-space: nowrap;
    min-width: 70px;
}

.room-cell {
    font-weight: 600;
    min-width: 60px;
}

.patient-cell {
    min-width: 150px;
}

.patient-link {
    color: #333;
    text-decoration: none;
}

.patient-link:hover {
    color: #439798;
    text-decoration: underline;
}

.cancelled-text {
    text-decoration: line-through;
    color: #999;
}

.ward-cell {
    min-width: 100px;
}

.treatment-cell {
    min-width: 200px;
}

.treatment-details {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
}

.detail-item {
    font-size: 0.85rem;
}

.dc-memo {
    color: #e74c3c;
    font-style: italic;
}

.staff-cell {
    min-width: 80px;
    font-size: 0.85rem;
}

/* Action Buttons */
.action-cell {
    min-width: 100px;
}

.action-buttons {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
}

.action-buttons a {
    display: inline-flex;
    padding: 0.25rem;
}

.action-icon {
    font-size: 1.1rem;
    transition: all 0.2s ease;
}

.view-icon {
    color: #31C872;
}

.view-icon:hover {
    color: #27ae60;
    transform: scale(1.1);
}

.edit-icon {
    color: #015666;
}

.edit-icon:hover {
    color: #013d4a;
    transform: scale(1.1);
}

.delete-icon {
    color: #e74c3c;
}

.delete-icon:hover {
    color: #c0392b;
    transform: scale(1.1);
}

/* No Results */
.no-results {
    padding: 4rem 2rem;
    color: #999;
}

.no-results-icon {
    font-size: 4rem;
    color: #ddd;
    margin-bottom: 1rem;
}

.no-results-text {
    font-size: 1.1rem;
    margin: 0;
}

/* Responsive */
@media (max-width: 768px) {
    .filtered-list-container {
        padding: 1rem;
    }

    .filter-criteria {
        flex-direction: column;
        gap: 0.5rem;
    }

    .filtered-table {
        font-size: 0.8rem;
    }

    .filtered-table th,
    .filtered-table td {
        padding: 8px 6px;
    }

    .action-icon {
        font-size: 1rem;
    }
}

/* Print Button Styles */
.summary-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
}

.print-button {
    white-space: nowrap;
}

.print-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* Responsive: Stack button below title on mobile */
@media (max-width: 768px) {
    .summary-header {
        flex-direction: column;
        align-items: flex-start;
    }

    .print-button {
        width: 100%;
    }
}
</style>
